import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  MenuItem,
  TableBody,
  TableCell,
  TableRow,
  ThemeProvider,
  Toolbar,
  createTheme,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import Moment from "react-moment";
import ActionToast from "../../../shared/components/ActionToast";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import Loader from "../../../shared/components/Loader";
import { NotificationMessage, getName } from "../../../shared/models/models";
import Table from "../../../shared/components/tables/Table";
import SortTableHead, { TableColumnDefinition } from "../../../shared/components/SortTableHead";
import TablePagination from "../../../shared/components/tables/TablePagination";
import ImageBox from "../../../shared/components/ImageBox";
import TableActionButton from "../../../shared/components/tables/TableActionButton";
import { useConfirmation } from "../../../shared/components/confirmation/ConfirmationProvider";
import MenuButton from "../../../shared/components/MenuButton";
import { AdminProductsState, Product, ProductsQueryParameter } from "../../models/AdminProductsModels";
import {
  changeQuery,
  deleteProduct,
  getProducts,
  selectProduct,
  setError,
  setSuccess,
  getManufacturers,
  getCategories,
} from "../../adminProductsSlice";
import ProductDialog from "./ProductDialog";
import AdminSearch from "../AdminSearch";

const Products = () => {
  const { query, products, product, loading, success, error } = useAppSelector<AdminProductsState>(
    (state: any) => state.adminProducts);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const confirm = useConfirmation();
  const [openProductDialog, setOpenProductDialog] = useState(false);

  const columns: TableColumnDefinition[] = [
    { title: "Image" },
    { title: "Name", parameter: ProductsQueryParameter.Name },
    { title: "Product number", parameter: ProductsQueryParameter.Number },
    { title: "Category" },
    { title: "Created", parameter: ProductsQueryParameter.CreatedDate },
    { title: "Last activity", parameter: ProductsQueryParameter.ModifiedDate },
    { title: undefined }
  ];

  const handleAdd = () => {
    dispatch(selectProduct(null));
    setOpenProductDialog(true);
  };

  const handleEdit = (product: Product) => {
    dispatch(selectProduct(product));
    setOpenProductDialog(true);
  };

  const handleDelete = async (product: Product) => {
    if (product) {
      await dispatch(deleteProduct(product.id));
      await dispatch(getProducts(query));
    }
  };

  const handleUploadFile = () => {
    navigate("/admin/catalog/upload");
  };

  const handleSearch = (value?: string) => {
    const newQuery = { ...query, search: value, pageNo: 1 };
    dispatch(changeQuery(newQuery));
    dispatch(getProducts(newQuery));
  };

  const handleSort = (parameter: number) => {
    const sortDesc = query.sortBy !== parameter ? false : !query.sortDesc;
    const newQuery = { ...query, sortBy: parameter, sortDesc: sortDesc };

    dispatch(changeQuery(newQuery))
    dispatch(getProducts(newQuery));
  };

  const handlePageSizeChange = async (size: number) => {
    const newQuery = { ...query, pageNo: 1, pageSize: size };
    await dispatch(changeQuery(newQuery));
    dispatch(getProducts(newQuery));
  };

  const handlePageNoChange = async (no: number) => {
    const newQuery = { ...query, pageNo: no };
    await dispatch(changeQuery(newQuery));
    dispatch(getProducts(newQuery));
  };

  const init = () => {
    dispatch(getProducts(query));
    dispatch(getManufacturers());
    dispatch(getCategories());
  };

  useEffect(() => {
    init();
  }, []);

  const theme = createTheme({
    components: {
      MuiToolbar: {
        styleOverrides: {
          root: {
            background: "#fff",
            borderBottom: "1px solid #d5d6d9",
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            boderRadius: "6px 0 0 6px !important",
          },
        },
      },
    },
  });

  if (loading) {
    return <Loader />;
  }

  return (
    <ThemeProvider theme={theme}>
      <ActionToast
        open={!!success}
        variant="success"
        message={success || ""}
        onClose={() => dispatch(setSuccess(undefined))}
        duration={6000}
      />

      <ActionToast
        open={!!error}
        variant="error"
        message={NotificationMessage.error(error).message || ""}
        onClose={() => dispatch(setError(undefined))}
        duration={6000}
      />

      <Toolbar className="flex items-center justify-between w-full">
        <div className="flex items-center">
          <p className="heading-2">Products</p>
          <div className="ml-7">
            <AdminSearch value={query.search} onSearch={handleSearch} />
          </div>
        </div>

        <MenuButton title="Manage products">
          <MenuItem onClick={handleUploadFile}>Upload Excel file</MenuItem>
          <MenuItem onClick={handleAdd}>Add single item</MenuItem>
        </MenuButton>
      </Toolbar>

      {products && products?.items.length > 0 && (
        <>
          <Table className="px-3 v-fill">
            <SortTableHead columns={columns} sortBy={query.sortBy} sortDesc={query.sortDesc} onSort={handleSort} />

            <TableBody>
              {products?.items.map((product) => (
                <TableRow key={product.id}>
                  <TableCell>
                    <ImageBox size="small" src={product.file?.content} />
                  </TableCell>

                  <TableCell>
                    <Box className="text-14-medium" color={"#333333"}>{product.manufacturer.name}</Box>
                    <Box className="text-14-medium" color={"#333333"}>{product.number}</Box>
                  </TableCell>

                  <TableCell>
                    <Box className="text-14-medium">{product.number}</Box>
                  </TableCell>

                  <TableCell>{product.category.name}</TableCell>

                  <TableCell>
                    <Moment date={product.createdDate} format="DD.MM.YYYY HH:mm" />
                  </TableCell>

                  <TableCell>
                    <Moment date={product.modifiedDate} format="DD.MM.YYYY HH:mm" />
                  </TableCell>

                  <TableCell>
                    <TableActionButton>
                      <MenuItem onClick={() => handleEdit(product)}>
                        <EditOutlinedIcon />
                        Edit
                      </MenuItem>

                      <MenuItem onClick={() => confirm({
                        title: "Delete product",
                        text: `Are you sure you want to delete ${getName(product.manufacturer.name, product.number)} product?`,
                        confirmButton: "Yes, delete",
                        closeButton: "No",
                        severity: "error",
                        onConfirm: () => handleDelete(product)
                      })}>
                        <DeleteOutlinedIcon />
                        Delete
                      </MenuItem>
                    </TableActionButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <TablePagination
            variant="bottom"
            pageNo={query.pageNo}
            pageSize={query.pageSize}
            totalCount={products.totalCount}
            onPageNoChange={handlePageNoChange}
            onPageSizeChange={handlePageSizeChange} />
        </>
      )}

      {products && products?.items.length < 1 && (
        <div className="text-center p-4">
          <p className="heading-1">You don't have any products</p>
        </div>
      )}

      <ProductDialog
        key={`product-dialog-${openProductDialog}`}
        open={openProductDialog}
        onClose={() => {
          setOpenProductDialog(false);
          dispatch(getProducts(query));
        }}
        product={product}
      />
    </ThemeProvider>
  );
};

export default Products;
